import React, { FunctionComponent, ReactNode, useRef, useState } from 'react'
import styles from './style.module.css'
import { CARET_PLACEMENTS, Heading, Icon, Text, Tooltip, useIsBreakpointMediumAndUp } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money } from '../Money'
import { useAppRouter } from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { Money as MoneyType } from '@afterpay/types'

interface CreditLimitProps {
  creditLimit?: MoneyType
}

export const ApprovedCreditLimit: FunctionComponent<CreditLimitProps> = ({ creditLimit }) => {
  const { locale } = useAppRouter()
  const { t } = useTranslation()
  const { smallTextSize, textSize } = useCheckoutSizes()
  const [showTooltip, setShowTooltip] = useState(false)
  const buttonRef = useRef(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState(false)
  const isDesktop = useIsBreakpointMediumAndUp() ?? false
  const [hasClickedOutside, setHasClickedOutside] = useState<boolean>(false)
  const id = 'credit-limit-tooltip'

  const toggleTooltip = (): void => {
    if (!hasSentAnalyticsData) {
      logEvent(TrackingEvent.PRESSED_CL_APPROVED_CREDIT_PAGE_CREDIT_LIMIT_TOOLTIP)
      setHasSentAnalyticsData(true)
    }

    if (!hasClickedOutside) {
      setShowTooltip(!showTooltip)
    }
    setHasClickedOutside(false)
  }

  const checkIfOutsideClick = (): void => {
    setHasClickedOutside(false)
  }

  const handleTooltipClose = (): void => {
    setShowTooltip(false)
  }

  const toolTip = (): ReactNode => (
    <Text size={smallTextSize} testNameSpace='credit-limit-terms'>
      {t('consumerLending:approvedCredit.creditLimit.tooltip')}
    </Text>
  )

  return (
    <div className={styles.approvedCreditLimit}>
      <Heading size={textSize}>{t('consumerLending:approvedCredit.creditLimit.heading')}</Heading>
      <div className={styles.approvedCreditBodyContent}>
        <Text size={'S'}>
          <Money value={creditLimit} locale={locale} size={'S'} />
        </Text>
        <button
          className={styles.tooltipButtonBase}
          type='button'
          onClick={toggleTooltip}
          onMouseDown={checkIfOutsideClick}
          data-testid='credit-limit-info-button'
          ref={buttonRef}
          aria-label={t('consumerLending:approvedCredit.creditLimit.ariaLabel')}
          aria-describedby={id}
          aria-expanded={showTooltip}
        >
          <Icon.Info size='ExtraSmall' />
        </button>
      </div>
      {showTooltip && (
        <Tooltip
          id={id}
          triggerRef={buttonRef}
          caretPlacement={CARET_PLACEMENTS.BOTTOM_START}
          testNameSpace='credit-limit-tooltip'
          usePortal={false}
          useMobileView={!isDesktop}
          content={toolTip()}
          skidding={80}
          onClose={handleTooltipClose}
          closeOnOutsideClick={setHasClickedOutside}
        />
      )}
    </div>
  )
}
